// src/components/payroll/employees/EmployeePayrollList.js
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, orderBy, getDocs, limit, 
  startAfter, doc, getDoc, deleteDoc
} from 'firebase/firestore';
import { useCurrency } from '../../../utils/currencyUtils';
import {
  Search, PlusCircle, Users, AlertCircle, 
  MoreHorizontal, Edit, Trash2, ChevronRight, 
  UserPlus, AlertTriangle
} from 'lucide-react';

const EmployeePayrollList = () => {
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();
  
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [actionMenu, setActionMenu] = useState(null);
  
  // Fetch employees with payroll details
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!currentUser || !activeBusiness) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch employee payroll details
        const payrollQuery = query(
          collection(db, 'employeePayrollDetails'),
          where('businessId', '==', activeBusiness.id),
          orderBy('updatedAt', 'desc'),
          limit(15)
        );
        
        const payrollSnapshot = await getDocs(payrollQuery);
        const employeesList = [];
        const employeeIds = [];
        
        payrollSnapshot.forEach((doc) => {
          const data = doc.data();
          employeeIds.push(data.employeeId);
          employeesList.push({
            id: data.employeeId,
            payrollId: doc.id,
            ...data
          });
        });
        
        setLastVisible(payrollSnapshot.docs[payrollSnapshot.docs.length - 1] || null);
        setHasMore(payrollSnapshot.docs.length === 15);
        
        // Fetch employee details
        if (employeeIds.length > 0) {
          // Get employee details from teamMembers collection
          const employeePromises = employeeIds.map(async (id) => {
            const empRef = doc(db, 'teamMembers', id);
            return getDoc(empRef);
          });
          
          const employeeDocs = await Promise.all(employeePromises);
          
          // Update employee data with full details
          employeeDocs.forEach((empDoc) => {
            if (empDoc.exists()) {
              const empData = empDoc.data();
              const index = employeesList.findIndex(e => e.id === empDoc.id);
              
              if (index !== -1) {
                employeesList[index] = {
                  ...employeesList[index],
                  firstName: empData.firstName || '',
                  lastName: empData.lastName || '',
                  email: empData.email || '',
                  position: empData.position || 'Employee'
                };
              }
            }
          });
        }
        
        setEmployees(employeesList);
        
      } catch (err) {
        console.error('Error fetching employees:', err);
        setError('Failed to load employees. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmployees();
  }, [currentUser, activeBusiness]);
  
  // Load more employees
  const handleLoadMore = async () => {
    if (!hasMore || !lastVisible || loading) return;
    
    try {
      setLoading(true);
      
      const payrollQuery = query(
        collection(db, 'employeePayrollDetails'),
        where('businessId', '==', activeBusiness.id),
        orderBy('updatedAt', 'desc'),
        startAfter(lastVisible),
        limit(15)
      );
      
      const payrollSnapshot = await getDocs(payrollQuery);
      const newEmployeesList = [];
      const newEmployeeIds = [];
      
      payrollSnapshot.forEach((doc) => {
        const data = doc.data();
        newEmployeeIds.push(data.employeeId);
        newEmployeesList.push({
          id: data.employeeId,
          payrollId: doc.id,
          ...data
        });
      });
      
      setLastVisible(payrollSnapshot.docs[payrollSnapshot.docs.length - 1] || null);
      setHasMore(payrollSnapshot.docs.length === 15);
      
      // Fetch employee details
      if (newEmployeeIds.length > 0) {
        const employeePromises = newEmployeeIds.map(async (id) => {
          const empRef = doc(db, 'teamMembers', id);
          return getDoc(empRef);
        });
        
        const employeeDocs = await Promise.all(employeePromises);
        
        employeeDocs.forEach((empDoc) => {
          if (empDoc.exists()) {
            const empData = empDoc.data();
            const index = newEmployeesList.findIndex(e => e.id === empDoc.id);
            
            if (index !== -1) {
              newEmployeesList[index] = {
                ...newEmployeesList[index],
                firstName: empData.firstName || '',
                lastName: empData.lastName || '',
                email: empData.email || '',
                position: empData.position || 'Employee'
              };
            }
          }
        });
      }
      
      setEmployees(prev => [...prev, ...newEmployeesList]);
      
    } catch (err) {
      console.error('Error loading more employees:', err);
      setError('Failed to load more employees. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete employee payroll details
  const handleDeletePayrollDetails = async (payrollId) => {
    if (!window.confirm('Are you sure you want to delete this pay run? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'employeePayrollDetails', payrollId));
      
      // Update local state
      setEmployees(prev => prev.filter(emp => emp.payrollId !== payrollId));
      
      // Close action menu
      setActionMenu(null);
      
    } catch (err) {
      console.error('Error deleting payroll details:', err);
      alert('Failed to delete payroll details. Please try again.');
    }
  };
  
  // Filter employees by search term
  const getFilteredEmployees = () => {
    if (!searchTerm.trim()) return employees;
    
    const searchLower = searchTerm.toLowerCase();
    return employees.filter(emp => 
      (emp.firstName && emp.firstName.toLowerCase().includes(searchLower)) ||
      (emp.lastName && emp.lastName.toLowerCase().includes(searchLower)) ||
      (emp.email && emp.email.toLowerCase().includes(searchLower)) ||
      (emp.position && emp.position.toLowerCase().includes(searchLower))
    );
  };
  
  const filteredEmployees = getFilteredEmployees();
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header with search and add button */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <Link
            to="/payroll/employees/new"
            className="px-4 py-2.5 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] flex items-center"
          >
            <PlusCircle size={18} className="mr-2" />
            Add Employee
          </Link>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        {loading && employees.length === 0 ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-gray-300 mb-4" />
            {employees.length === 0 ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No employees with payroll details</h3>
                <p className="text-gray-500 mb-6">
                  Start by adding payroll details for your employees to process payroll.
                </p>
                <Link
                  to="/payroll/employees/new"
                  className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-flex items-center"
                >
                  <UserPlus size={18} className="mr-2" />
                  Add Employee Payroll
                </Link>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching employees</h3>
                <p className="text-gray-500 mb-4">
                  No employees match your search criteria.
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear Search
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tax ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Salary Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Salary Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bank Info
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEmployees.map((employee) => (
                    <tr key={employee.payrollId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-700">
                            {employee.firstName ? employee.firstName.charAt(0) : ""}
                            {employee.lastName ? employee.lastName.charAt(0) : ""}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {employee.firstName} {employee.lastName}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              {employee.position || 'Employee'} • {employee.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {employee.taxIdentifier || (
                          <span className="text-amber-500 flex items-center justify-center">
                            <AlertTriangle size={14} className="mr-1" /> Missing
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center capitalize">
                        {employee.salaryType || 'Not set'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {employee.baseSalary
                          ? employee.salaryType === 'hourly'
                            ? `${formatAmount(employee.baseSalary)}/hr`
                            : employee.salaryType === 'annual'
                              ? `${formatAmount(employee.baseSalary)}/year`
                              : formatAmount(employee.baseSalary)
                          : 'Not set'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        {employee.bankDetails?.accountNumber ? (
                          <span className="text-gray-900">
                            {employee.bankDetails.bankName} •••• 
                            {employee.bankDetails.accountNumber.slice(-4)}
                          </span>
                        ) : (
                          <span className="text-amber-500 flex items-center justify-end">
                            <AlertTriangle size={14} className="mr-1" /> Missing
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/payroll/employees/${employee.payrollId}`}
                            className="text-[#190192] hover:text-[#2C0DB5]"
                          >
                            View
                          </Link>
                          <div className="relative">
                            <button
                              onClick={() => setActionMenu(actionMenu === employee.payrollId ? null : employee.payrollId)}
                              className="text-gray-400 hover:text-gray-600 p-1"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                            
                            {actionMenu === employee.payrollId && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                <div className="py-1">
                                  <Link
                                    to={`/payroll/employees/${employee.payrollId}`}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    <ChevronRight size={16} className="mr-2 text-gray-500" />
                                    View Details
                                  </Link>
                                  <Link
                                    to={`/payroll/employees/${employee.payrollId}/edit`}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    <Edit size={16} className="mr-2 text-gray-500" />
                                    Edit
                                  </Link>
                                  <button
                                    className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                                    onClick={() => handleDeletePayrollDetails(employee.payrollId)}
                                  >
                                    <Trash2 size={16} className="mr-2 text-red-500" />
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Load more button */}
            {hasMore && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className={`px-4 py-2 border border-gray-300 rounded-lg ${
                    loading ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="inline-block animate-spin h-4 w-4 border-2 border-gray-500 rounded-full border-t-transparent mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeePayrollList;